/**
 * GraphValidator — Base validation utilities for graphs.
 *
 * Provides common validation logic that can be extended
 * by domain-specific validators (Redprint, VFX, etc.)
 */

import type {
  BaseGraphData,
  BaseGraphVariable,
  BaseNodeInstance,
  BaseNodeDefinition,
  BasePortDefinition,
  GraphDiagnostic,
} from './types';
import { Graph } from './Graph';

// ============================================================================
// Validation result
// ============================================================================

/**
 * Result of validating a graph.
 */
export interface ValidationResult {
  readonly valid: boolean;
  readonly diagnostics: readonly GraphDiagnostic[];
}

// ============================================================================
// Node registry interface
// ============================================================================

/**
 * Interface for a node registry that can resolve node definitions.
 * Domain-specific registries implement this.
 */
export interface NodeDefinitionResolver<
  TDef extends BaseNodeDefinition = BaseNodeDefinition,
> {
  /** Check if a node type is registered */
  has(type: string): boolean;

  /** Get a node definition by type */
  get(type: string): TDef | undefined;

  /** Resolve an input port on a node type */
  resolveInputPort(type: string, portId: string): BasePortDefinition | undefined;

  /** Resolve an output port on a node type */
  resolveOutputPort(type: string, portId: string): BasePortDefinition | undefined;
}

// ============================================================================
// Connection check interface
// ============================================================================

/**
 * Result of checking if two ports can connect.
 */
export type ConnectionCheck =
  | { readonly ok: true; readonly coercion?: string | null }
  | { readonly ok: false; readonly reason: string };

/**
 * Function that checks if two ports can connect.
 */
export type CanConnectFn = (
  fromPort: BasePortDefinition,
  toPort: BasePortDefinition,
) => ConnectionCheck;

// ============================================================================
// Base validator
// ============================================================================

/**
 * Options for the base graph validator.
 */
export interface ValidatorOptions<TDef extends BaseNodeDefinition = BaseNodeDefinition> {
  /** Node registry for resolving node types */
  registry: NodeDefinitionResolver<TDef>;

  /** Optional function to check port compatibility */
  canConnect?: CanConnectFn;

  /** If true, checks for cycles in data flow (default: true) */
  checkDataCycles?: boolean;

  /**
   * Function to determine if a port type is a "flow" type (execution order).
   * Flow ports are excluded from cycle detection.
   */
  isFlowType?: (type: string) => boolean;
}

/**
 * Base graph validator with common validation passes.
 * Can be extended for domain-specific validation.
 */
export class GraphValidator<TDef extends BaseNodeDefinition = BaseNodeDefinition> {
  protected readonly _registry: NodeDefinitionResolver<TDef>;
  protected readonly _canConnect?: CanConnectFn;
  protected readonly _checkDataCycles: boolean;
  protected readonly _isFlowType: (type: string) => boolean;

  constructor(options: ValidatorOptions<TDef>) {
    this._registry = options.registry;
    this._canConnect = options.canConnect;
    this._checkDataCycles = options.checkDataCycles ?? true;
    this._isFlowType = options.isFlowType ?? (() => false);
  }

  /**
   * Run all validation passes on a graph.
   */
  validate<TNode extends BaseNodeInstance, TVariable extends BaseGraphVariable>(
    graph: Graph<TNode, TVariable>,
  ): ValidationResult {
    const diags: GraphDiagnostic[] = [];

    this._checkNodeTypes(graph, diags);
    this._checkEdges(graph, diags);

    if (this._checkDataCycles) {
      this._checkCycles(graph, diags);
    }

    return {
      valid: diags.every(d => d.severity !== 'error'),
      diagnostics: diags,
    };
  }

  // --------------------------------------------------------------------------
  // Pass 1: Node type existence
  // --------------------------------------------------------------------------

  protected _checkNodeTypes<TNode extends BaseNodeInstance, TVariable extends BaseGraphVariable>(
    graph: Graph<TNode, TVariable>,
    diags: GraphDiagnostic[],
  ): void {
    for (const node of graph.getNodes()) {
      if (!this._registry.has(node.type)) {
        diags.push({
          severity: 'error',
          message: `Unknown node type '${node.type}'`,
          nodeId: node.id,
        });
      }
    }
  }

  // --------------------------------------------------------------------------
  // Pass 2: Edge validity + type compatibility
  // --------------------------------------------------------------------------

  protected _checkEdges<TNode extends BaseNodeInstance, TVariable extends BaseGraphVariable>(
    graph: Graph<TNode, TVariable>,
    diags: GraphDiagnostic[],
  ): void {
    for (const edge of graph.getEdges()) {
      const fromNode = graph.getNode(edge.from.nodeId);
      const toNode = graph.getNode(edge.to.nodeId);

      if (!fromNode) {
        diags.push({
          severity: 'error',
          message: `Edge references missing source node '${edge.from.nodeId}'`,
        });
        continue;
      }
      if (!toNode) {
        diags.push({
          severity: 'error',
          message: `Edge references missing target node '${edge.to.nodeId}'`,
        });
        continue;
      }

      const fromPort = this._registry.resolveOutputPort(fromNode.type, edge.from.portId);
      const toPort = this._registry.resolveInputPort(toNode.type, edge.to.portId);

      if (!fromPort) {
        diags.push({
          severity: 'error',
          message: `Node '${fromNode.type}' has no output port '${edge.from.portId}'`,
          nodeId: fromNode.id,
          portId: edge.from.portId,
        });
        continue;
      }
      if (!toPort) {
        diags.push({
          severity: 'error',
          message: `Node '${toNode.type}' has no input port '${edge.to.portId}'`,
          nodeId: toNode.id,
          portId: edge.to.portId,
        });
        continue;
      }

      // Type compatibility (if checker provided)
      if (this._canConnect) {
        const check = this._canConnect(fromPort, toPort);
        if (!check.ok) {
          diags.push({
            severity: 'error',
            message: `Incompatible connection: ${(check as { ok: false; reason: string }).reason}`,
            nodeId: toNode.id,
            portId: toPort.id,
          });
        }
      }
    }
  }

  // --------------------------------------------------------------------------
  // Pass 3: Data-flow cycle detection (DFS)
  // --------------------------------------------------------------------------

  protected _checkCycles<TNode extends BaseNodeInstance, TVariable extends BaseGraphVariable>(
    graph: Graph<TNode, TVariable>,
    diags: GraphDiagnostic[],
  ): void {
    // Build data-only adjacency: nodeId → downstream node IDs (via data edges)
    const adj = new Map<string, Set<string>>();

    for (const edge of graph.getEdges()) {
      const fromNode = graph.getNode(edge.from.nodeId);
      if (!fromNode) continue;

      const fromPort = this._registry.resolveOutputPort(fromNode.type, edge.from.portId);
      if (!fromPort || this._isFlowType(fromPort.type)) continue; // skip flow edges

      let set = adj.get(edge.from.nodeId);
      if (!set) {
        set = new Set();
        adj.set(edge.from.nodeId, set);
      }
      set.add(edge.to.nodeId);
    }

    // Detect cycles via DFS with coloring
    const WHITE = 0, GRAY = 1, BLACK = 2;
    const color = new Map<string, number>();
    for (const n of graph.getNodes()) color.set(n.id, WHITE);

    const dfs = (nodeId: string): boolean => {
      color.set(nodeId, GRAY);
      const neighbors = adj.get(nodeId);
      if (neighbors) {
        for (const next of neighbors) {
          const c = color.get(next) ?? WHITE;
          if (c === GRAY) return true;  // back edge = cycle
          if (c === WHITE && dfs(next)) return true;
        }
      }
      color.set(nodeId, BLACK);
      return false;
    };

    for (const n of graph.getNodes()) {
      if (color.get(n.id) === WHITE && dfs(n.id)) {
        diags.push({
          severity: 'error',
          message: 'Data-flow cycle detected — data connections cannot form loops',
          nodeId: n.id,
        });
        break; // one diagnostic is enough
      }
    }
  }
}

// ============================================================================
// Topological sort utility
// ============================================================================

/**
 * Perform a topological sort on nodes (data dependencies only).
 * Returns nodes in execution order, or null if there's a cycle.
 */
export function topologicalSort<TNode extends BaseNodeInstance, TVariable extends BaseGraphVariable>(
  graph: Graph<TNode, TVariable>,
  registry: NodeDefinitionResolver,
  isFlowType: (type: string) => boolean = () => false,
): TNode[] | null {
  // Build in-degree map for data edges only
  const inDegree = new Map<string, number>();
  const adj = new Map<string, string[]>();

  for (const node of graph.getNodes()) {
    inDegree.set(node.id, 0);
    adj.set(node.id, []);
  }

  for (const edge of graph.getEdges()) {
    const fromNode = graph.getNode(edge.from.nodeId);
    if (!fromNode) continue;

    const fromPort = registry.resolveOutputPort(fromNode.type, edge.from.portId);
    if (!fromPort || isFlowType(fromPort.type)) continue; // skip flow

    adj.get(edge.from.nodeId)!.push(edge.to.nodeId);
    inDegree.set(edge.to.nodeId, (inDegree.get(edge.to.nodeId) ?? 0) + 1);
  }

  // Kahn's algorithm
  const queue: string[] = [];
  for (const [id, deg] of inDegree) {
    if (deg === 0) queue.push(id);
  }

  const sorted: TNode[] = [];
  while (queue.length > 0) {
    const id = queue.shift()!;
    const node = graph.getNode(id);
    if (node) sorted.push(node);

    for (const next of adj.get(id) ?? []) {
      const newDeg = (inDegree.get(next) ?? 1) - 1;
      inDegree.set(next, newDeg);
      if (newDeg === 0) queue.push(next);
    }
  }

  // If not all nodes are in sorted, there's a cycle
  if (sorted.length !== graph.nodeCount) return null;

  return sorted;
}
